import { useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { withTranslation, TFunction } from "react-i18next";
import Icon from '../../common/Icon';

type ActiveKey = "features" | "circles" | "how-it-works" | "/support" | "/about" | "/contact" | "/feedback" | null;

const SECTIONS: Exclude<ActiveKey, null | `/${string}`>[] = ["features", "circles", "how-it-works"];

const Header = ({ t }: { t: TFunction }) => {
  const [visible, setVisibility] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState<ActiveKey>(null);
  const history = useHistory();
  const location = useLocation();

  const onHome = location.pathname === "/" || location.pathname === "/home";
  const solid = scrolled || !onHome;

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
      if (onHome) {
        let current: ActiveKey = null;
        for (const id of SECTIONS) {
          const el = document.getElementById(id);
          if (el && el.getBoundingClientRect().top <= 160) current = id;
        }
        setActive(current);
      }
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname, onHome]);

  // Route changes always set the active item, even before any scrolling.
  useEffect(() => {
    if (!onHome) {
      const route = location.pathname;
      const known: ActiveKey[] = ["/support", "/about", "/contact", "/feedback"];
      setActive(known.includes(route as ActiveKey) ? (route as ActiveKey) : null);
    }
  }, [location.pathname, onHome]);

  // The menu panel is plain markup now, so it owns its own escape/scroll lock.
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setVisibility(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [visible]);

  const toggleButton = () => setVisibility(!visible);

  const handleSectionNav = (sectionId: Exclude<ActiveKey, null | `/${string}`>) => {
    setVisibility(false);
    setActive(sectionId);
    if (onHome) {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      history.push("/");
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  };

  const navLinkCls = "link-underline text-ink/80 hover:text-ink font-semibold cursor-pointer no-underline bg-transparent border-none p-0";
  const navActiveCls = "link-underline link-underline-active text-ink font-bold cursor-pointer no-underline bg-transparent border-none p-0";
  const drawerLinkCls = "link-underline text-xl font-bold text-ink/80 hover:text-ink no-underline transition-colors text-left bg-transparent border-none cursor-pointer p-0";
  const drawerActiveCls = "link-underline link-underline-active text-xl font-bold text-ink no-underline transition-colors text-left bg-transparent border-none cursor-pointer p-0";

  const desktopCls = (key: ActiveKey) => (key !== null && key === active ? navActiveCls : navLinkCls);
  const drawerCls = (key: ActiveKey) => (key !== null && key === active ? drawerActiveCls : drawerLinkCls);
  const aria = (key: ActiveKey) => (key !== null && key === active ? { "aria-current": "page" as const } : {});

  return (
    <nav className={`fixed top-0 w-full z-50 transition-colors duration-300 ${solid ? "bg-white/85 backdrop-blur-xl border-b border-ink/10" : "bg-transparent border-b border-transparent"}`}>
      <div className="flex justify-between items-center max-w-7xl mx-auto px-4 sm:px-6 md:px-8 h-16 md:h-20">
        <Link to="/" className="flex items-center gap-2 no-underline" aria-label="Ready2Play home">
          <img src="/img/icons/r-mark-192.png" alt="Ready2Play Logo" width={192} height={192} className="h-9 md:h-11 w-auto object-contain" />
          <span className="text-xl md:text-2xl font-headline font-black text-ink">Ready2Play</span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8 lg:gap-10">
          <button onClick={() => handleSectionNav("features")} className={desktopCls("features")} {...aria("features")}>
            Find Games
          </button>
          <button onClick={() => handleSectionNav("circles")} className={desktopCls("circles")} {...aria("circles")}>
            Circles
          </button>
          <button onClick={() => handleSectionNav("how-it-works")} className={desktopCls("how-it-works")} {...aria("how-it-works")}>
            How It Works
          </button>
          <Link to="/support" className={desktopCls("/support")} onClick={() => setVisibility(false)} {...aria("/support")}>
            Support
          </Link>
          <Link to="/about" className={desktopCls("/about")} onClick={() => setVisibility(false)} {...aria("/about")}>
            About
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-4">
          <a
             href="https://apps.apple.com/app/apple-store/id6683305729"
             target="_blank"
             rel="noreferrer noopener"
             className="btn-solid bg-teal text-white px-5 md:px-6 py-2.5 rounded-xl font-bold hidden md:flex text-sm items-center gap-2 no-underline"
          >
            <Icon name="phone_iphone" className="text-base" />
            App Store
          </a>
          <a
             href="https://play.google.com/store/apps/details?id=com.rgs.ready2play"
             target="_blank"
             rel="noreferrer noopener"
             className="btn-solid bg-transparent text-ink border border-ink/50 px-5 md:px-6 py-2.5 rounded-xl font-bold hidden md:flex text-sm items-center gap-2 no-underline"
          >
            <Icon name="android" className="text-base" />
            Google Play
          </a>
        </div>

        {/* Mobile burger */}
        <button
          className="md:hidden flex flex-col gap-1.5 p-2 bg-transparent border-none cursor-pointer"
          onClick={toggleButton}
          aria-label="Open menu"
          aria-expanded={visible}
        >
          <span className="w-6 h-0.5 bg-ink block" />
          <span className="w-6 h-0.5 bg-ink block" />
          <span className="w-6 h-0.5 bg-ink block" />
        </button>
      </div>

      {/* Mobile menu. Hand-rolled instead of a Drawer component: it is a fixed
          panel and a mask, which does not justify shipping a UI kit. */}
      <div
        className={`md:hidden fixed inset-0 z-50 transition-opacity duration-200 ${visible ? "opacity-100" : "pointer-events-none opacity-0"}`}
        aria-hidden={!visible}
      >
        <div className="absolute inset-0 bg-ink/30" onClick={toggleButton} />
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          className={`absolute right-0 top-0 h-full w-[86%] max-w-sm bg-white shadow-2xl flex flex-col gap-6 p-8 overflow-y-auto transition-transform duration-300 ease-out ${visible ? "translate-x-0" : "translate-x-full"}`}
        >
          <button className="self-end text-ink text-2xl font-bold bg-transparent border-none cursor-pointer" onClick={toggleButton} aria-label="Close menu">✕</button>
          <button className={drawerCls("features")} onClick={() => handleSectionNav("features")} {...aria("features")}>Find Games</button>
          <button className={drawerCls("circles")} onClick={() => handleSectionNav("circles")} {...aria("circles")}>Circles</button>
          <button className={drawerCls("how-it-works")} onClick={() => handleSectionNav("how-it-works")} {...aria("how-it-works")}>How It Works</button>
          <Link className={drawerCls("/support")} to="/support" onClick={() => setVisibility(false)} {...aria("/support")}>Support</Link>
          <Link className={drawerCls("/about")} to="/about" onClick={() => setVisibility(false)} {...aria("/about")}>About</Link>
          <Link className={drawerCls("/contact")} to="/contact" onClick={() => setVisibility(false)} {...aria("/contact")}>Contact</Link>
          <Link className={drawerCls("/feedback")} to="/feedback" onClick={() => setVisibility(false)} {...aria("/feedback")}>Feedback</Link>
          <div className="mt-4 flex flex-col gap-4">
             <a
               href="https://apps.apple.com/app/apple-store/id6683305729"
               target="_blank"
               rel="noreferrer noopener"
               className="btn-solid bg-teal text-white px-6 py-3 rounded-xl font-bold flex items-center justify-center gap-2 no-underline"
             >
               <Icon name="phone_iphone" className="text-lg" />
               App Store
             </a>
             <a
               href="https://play.google.com/store/apps/details?id=com.rgs.ready2play"
               target="_blank"
               rel="noreferrer noopener"
               className="btn-solid bg-transparent text-ink border border-ink/50 px-6 py-3 rounded-xl font-bold flex items-center justify-center gap-2 no-underline"
             >
               <Icon name="android" className="text-lg" />
               Google Play
             </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default withTranslation()(Header);
